import { LucideIcon, Briefcase, UserCog, Cpu, ArrowRight } from 'lucide-react';
import { Link } from 'react-scroll';

interface Service {
  title: string;
  description: string;
  icon: LucideIcon;
  features: string[];
  outcome: string;
  example?: string;
  iconColor: string;
  iconBg: string;
  iconBorder: string;
}

const services: Service[] = [
  {
    title: "Fractional C-Suite",
    description: "Part-time CIO, COO, CTO, or CPO embedded in your business — with the product, program, and project management discipline to drive initiatives to completion.",
    icon: UserCog,
    features: ["Strategic advisor (1 day/week)", "Fractional leader (2–3 days/week)", "Product & program ownership", "Interim executive (4 days/week)"],
    outcome: "A trusted executive who drives outcomes, aligns your board, and keeps every initiative on track.",
    iconColor: "text-teal-deeper",
    iconBg: "bg-teal-light",
    iconBorder: "border-teal-border",
  },
  {
    title: "AI-Native Development",
    description: "We build AI agents and MCP-integrated systems that replace manual workflows. From NWS-backed field ops automation to Claude-powered internal tools — production-grade, not proof-of-concept.",
    icon: Cpu,
    features: ["MCP server & agent development", "API-first AI integration", "Claude & LLM workflow design", "Field operations automation"],
    outcome: "Working software in production — not a pilot deck. Typically first ship within 6 weeks.",
    example: "Built: MCP agent pulling NWS live conditions → AI-optimized routing → exec ops report. 47 field sites. Fully automated.",
    iconColor: "text-em-deeper",
    iconBg: "bg-em-light",
    iconBorder: "border-em-border",
  },
  {
    title: "Technology Assessment",
    description: "A focused 2-week engagement delivering an executive briefing on technology maturity, risk, and a prioritized action plan. Designed for leadership teams who need clarity fast.",
    icon: Briefcase,
    features: ["Executive briefing", "Maturity assessment", "90-day action plan"],
    outcome: "Clear risks, a prioritized roadmap, and exec alignment — all in two weeks.",
    iconColor: "text-em-deeper",
    iconBg: "bg-em-light",
    iconBorder: "border-em-border",
  },
];

export const Services = () => {
  return (
    <section id="services" className="bg-em-light py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div className="space-y-4 max-w-2xl">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white border border-em-border text-xs text-em-deeper font-semibold">
              How We Help
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
              The engagement that<br />fits where you are.
            </h2>
            <p className="text-slate-500 leading-relaxed">
              Fractional C-suite leadership, AI-native development, or a fast executive readout — every engagement is built to reduce risk and ship outcomes.
            </p>
          </div>
          <Link
            to="contact"
            smooth={true}
            duration={500}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-em-border bg-white text-sm text-em-deeper font-semibold hover:bg-em hover:text-white hover:border-em transition-all cursor-pointer whitespace-nowrap self-start lg:self-auto shadow-sm"
          >
            Start a conversation <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {services.map((service) => (
            <div
              key={service.title}
              className="group relative bg-white rounded-3xl border border-slate-100 shadow-card hover:shadow-card-hover hover:-translate-y-2 transition-all duration-300 overflow-hidden"
            >
              <div className="absolute inset-x-0 top-0 h-0.5 gradient-em opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="p-7">
                <div className={`h-11 w-11 rounded-2xl ${service.iconBg} border ${service.iconBorder} flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110`}>
                  <service.icon className={`w-5 h-5 ${service.iconColor}`} />
                </div>

                <h3 className="text-base font-bold text-slate-900 mb-2 leading-snug">{service.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-6">{service.description}</p>

                {service.example && (
                  <div className="rounded-2xl bg-slate-950 p-4 mb-4 font-mono">
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1.5">Live example</p>
                    <p className="text-[11px] text-em leading-relaxed">{service.example}</p>
                  </div>
                )}

                <div className="rounded-2xl bg-em-light border border-em-border p-4 mb-6">
                  <p className="text-[10px] text-em-dark font-semibold uppercase tracking-widest mb-1.5">Outcome</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{service.outcome}</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {service.features.map((f) => (
                    <span
                      key={f}
                      className="text-xs px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-500 group-hover:border-em-border group-hover:text-em-deeper transition-colors duration-300"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
